import type { Organization, OrganizationFormData, SquareLocation } from '../types';

// Always use relative URLs - Caddy proxy handles routing to backend
const API_BASE_URL = '';

interface AdminAPIResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

// ---------------------------------------------------------------------------
// Local domain types for admin API responses
// ---------------------------------------------------------------------------

interface OrgTokenUpdate {
  square_access_token?: string;
  square_application_id?: string;
  square_environment?: string;
  square_location_id?: string;
  ai_api_token?: string;
}

interface OrgMember {
  id: number;
  email: string;
  full_name: string;
  is_admin: boolean;
  can_access_square: boolean;
  can_access_ai: boolean;
  can_manage_users: boolean;
  can_manage_tokens: boolean;
  role?: string;
}

interface OrgMemberAdd {
  email: string;
  can_access_square?: boolean;
  can_access_ai?: boolean;
  can_manage_users?: boolean;
  role?: string;
}

interface OrgMemberPermissions {
  can_access_square?: boolean;
  can_access_ai?: boolean;
  can_manage_users?: boolean;
  can_manage_tokens?: boolean;
  role?: string;
}

interface AdminUser {
  id: number;
  email: string;
  full_name: string;
  picture?: string;
  is_admin: boolean;
  is_active: boolean;
  can_access_square: boolean;
  can_access_ai: boolean;
  created_at: string;
}

interface AdminUserCreate {
  email: string;
  full_name?: string;
  is_admin?: boolean;
  is_active?: boolean;
  can_access_square?: boolean;
  can_access_ai?: boolean;
}

// ---------------------------------------------------------------------------
// Core request helper
// ---------------------------------------------------------------------------

async function adminApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<AdminAPIResponse<T>> {
  try {
    const headers: Record<string, string> = {};
    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    // Add Bearer token from localStorage
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add content type for POST/PUT requests
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
    console.log(`Admin API request to: ${url}`);

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies
    });

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // If error response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }

      return {
        data: null as unknown as T,
        success: false,
        message: errorMessage,
      };
    }

    // Handle 204 No Content responses (no body to parse)
    if (response.status === 204) {
      return {
        data: null as unknown as T,
        success: true,
        message: 'Success',
      };
    }

    const data = await response.json();
    return {
      data,
      success: true,
    };
  } catch (error) {
    console.error('Admin API request error:', error);
    return {
      data: null as unknown as T,
      success: false,
      message: error instanceof Error ? error.message : 'Request failed',
    };
  }
}

// ---------------------------------------------------------------------------
// Organization API
// ---------------------------------------------------------------------------

export const organizationApi = {
  // List all organizations (admin only)
  async list() {
    return adminApiRequest<Organization[]>('/api/organizations/');
  },

  // Get single organization
  async get(id: string) {
    return adminApiRequest<Organization>(`/api/organizations/${id}`);
  },

  // Create new organization
  async create(data: OrganizationFormData) {
    return adminApiRequest<Organization>('/api/organizations/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update organization
  async update(id: string, data: OrganizationFormData) {
    return adminApiRequest<Organization>(`/api/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete organization
  async delete(id: string) {
    return adminApiRequest<{ success: boolean }>(`/api/organizations/${id}`, {
      method: 'DELETE',
    });
  },

  // Get my organizations
  async getMyOrganizations() {
    return adminApiRequest<Organization[]>('/api/organizations/my');
  },

  // Update organization tokens
  async updateTokens(id: string, tokens: OrgTokenUpdate) {
    return adminApiRequest<Organization>(`/api/organizations/${id}/tokens`, {
      method: 'PUT',
      body: JSON.stringify(tokens),
    });
  },

  // Get Square locations using credentials
  async getSquareLocations(credentials: {
    square_access_token: string;
    square_environment: string;
  }) {
    return adminApiRequest<{ locations: SquareLocation[] }>('/api/square/locations', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Validate AI token
  async validateAIToken(credentials: {
    ai_api_token: string;
    ai_provider: string;
  }) {
    return adminApiRequest<{ success: boolean; provider: string; message: string }>(
      '/api/ai/validate-token',
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    );
  },

  // Test AI token for organization
  async testAIToken(id: string, token?: string, model?: string) {
    const requestBody: { token?: string; model?: string } = {};
    if (token) requestBody.token = token;
    if (model) requestBody.model = model;

    return adminApiRequest<{
      success: boolean;
      message: string;
      provider?: string;
      model?: string;
      error_type?: string;
    }>(`/api/organizations/${id}/test-ai-token`, {
      method: 'POST',
      body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined,
    });
  },

  // Test Square connection with credentials
  async testSquareConnection(credentials: {
    square_access_token: string;
    square_environment: string;
  }) {
    return adminApiRequest<{ success: boolean; locations: SquareLocation[] }>(
      '/api/organizations/test-square-connection',
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    );
  },

  // Get organization members
  async getMembers(id: string) {
    return adminApiRequest<OrgMember[]>(`/api/organizations/${id}/users`);
  },

  // Add member to organization
  async addMember(id: string, data: OrgMemberAdd) {
    return adminApiRequest<OrgMember>(`/api/organizations/${id}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update member permissions
  async updateMemberPermissions(orgId: string, userId: number, permissions: OrgMemberPermissions) {
    return adminApiRequest<OrgMember>(`/api/organizations/${orgId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(permissions),
    });
  },

  // Remove member from organization
  async removeMember(orgId: string, userId: number) {
    return adminApiRequest<{ success: boolean }>(`/api/organizations/${orgId}/users/${userId}`, {
      method: 'DELETE',
    });
  },

  // Preview schema expanded prompt (accepts optional prompt and schema)
  // TODO(#15): ai_analysis_schema and schema response field are opaque JSON blobs — type in Wave 2C
  async previewSchema(
    id: string,
    ai_prompt?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ai_analysis_schema?: any
  ) {
    return adminApiRequest<{
      prompt_template: string;
      expanded_prompt: string;
      has_schema: boolean;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      schema?: any;
    }>(`/api/organizations/${id}/schema-preview`, {
      method: 'POST',
      body: JSON.stringify({
        ai_prompt: ai_prompt ?? null,
        ai_analysis_schema: ai_analysis_schema ?? null,
      }),
    });
  },

  // Preview API request that would be sent to Claude (accepts optional prompt and schema)
  // TODO(#15): ai_analysis_schema param and body response field are opaque — type in Wave 2C
  async previewApiRequest(
    id: string,
    ai_prompt?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ai_analysis_schema?: any
  ) {
    return adminApiRequest<{
      request: {
        url: string;
        method: string;
        headers: Record<string, string>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: any;
      };
      notes: {
        system_prompt_resolved: boolean;
        template_variables_expanded: boolean;
        image_count: number;
        token_estimate: {
          system_prompt_tokens: number;
          user_prompt_tokens: number;
          images_tokens: number;
          total_estimated: number;
        };
      };
    }>(`/api/organizations/${id}/api-request-preview`, {
      method: 'POST',
      body: JSON.stringify({
        ai_prompt: ai_prompt ?? null,
        ai_analysis_schema: ai_analysis_schema ?? null,
      }),
    });
  },

  // Export initial data as SQL
  async exportInitialData() {
    const exportHeaders: Record<string, string> = {};
    const exportToken = localStorage.getItem('auth_token');
    if (exportToken) {
      exportHeaders['Authorization'] = `Bearer ${exportToken}`;
    }

    const response = await fetch('/api/organizations/export-initial-data', {
      method: 'GET',
      credentials: 'include',
      headers: exportHeaders,
    });

    if (!response.ok) {
      throw new Error('Failed to export initial data');
    }

    // Get the filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'initial_data.sql';

    // Get the blob
    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return { success: true, message: 'Data exported successfully' };
  },
};

// ---------------------------------------------------------------------------
// User API
// ---------------------------------------------------------------------------

export const userApi = {
  // List all users (admin only)
  async list() {
    return adminApiRequest<AdminUser[]>('/api/users/');
  },

  // Create new user
  async create(data: AdminUserCreate) {
    return adminApiRequest<AdminUser>('/api/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get single user
  async get(id: string) {
    return adminApiRequest<AdminUser>(`/api/users/${id}`);
  },

  // Update user
  async update(id: string, data: Partial<AdminUserCreate>) {
    return adminApiRequest<AdminUser>(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete user
  async delete(id: string) {
    return adminApiRequest<{ success: boolean }>(`/api/users/${id}`, {
      method: 'DELETE',
    });
  },
};

// ---------------------------------------------------------------------------
// AI Models & Providers API
// TODO(#15): Provider/model shapes come from an external AI model registry —
//            type fully in Wave 2D once backend schema is stable.
// ---------------------------------------------------------------------------

export const aiModelsApi = {
  // Providers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async listProviders(): Promise<AdminAPIResponse<any[]>> {
    return adminApiRequest('/api/ai-models/providers');
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createProvider(data: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return adminApiRequest<any>('/api/ai-models/providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateProvider(id: string, data: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return adminApiRequest<any>(`/api/ai-models/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteProvider(id: string) {
    return adminApiRequest<{ success: boolean }>(`/api/ai-models/providers/${id}`, {
      method: 'DELETE',
    });
  },

  // Models
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async listModels(): Promise<AdminAPIResponse<any[]>> {
    return adminApiRequest('/api/ai-models/models');
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createModel(data: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return adminApiRequest<any>('/api/ai-models/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateModel(id: string, data: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return adminApiRequest<any>(`/api/ai-models/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteModel(id: string) {
    return adminApiRequest<{ success: boolean }>(`/api/ai-models/models/${id}`, {
      method: 'DELETE',
    });
  },
};
